import { Directive, Input, TemplateRef, ViewContainerRef, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { PermissionService } from '../services/permission.service';
import { ProfileService } from '../../profile/profile.service';

/**
 * Structural directive to conditionally show/hide elements based on permissions
 * 
 * Usage examples:
 * *hasPermission="'course.create'" - Show if user has permission
 * *hasPermission="['course.create', 'course.update']" - Show if user has any of these permissions
 * *hasPermission="'admin'; role: true" - Show if user has admin role
 * *hasPermission="'instructor'; roleLevel: true" - Show if user is instructor or above
 */
@Directive({
  selector: '[hasPermission]',
  standalone: true
})
export class HasPermissionDirective implements OnInit, OnDestroy {
  @Input() set hasPermission(permissions: string | string[]) {
    this.permissions = Array.isArray(permissions) ? permissions : [permissions];
    this.updateView();
  }

  @Input() set hasPermissionRole(isRoleCheck: boolean) {
    this.isRoleCheck = isRoleCheck;
    this.updateView();
  }

  @Input() set hasPermissionRoleLevel(isRoleLevelCheck: boolean) {
    this.isRoleLevelCheck = isRoleLevelCheck;
    this.updateView();
  }

  @Input() set hasPermissionRequireAll(requireAll: boolean) {
    this.requireAllPermissions = requireAll;
    this.updateView();
  }

  @Input() set hasPermissionElse(template: TemplateRef<any>) {
    this.elseTemplate = template;
    this.updateView();
  }

  private permissions: string[] = [];
  private isRoleCheck = false;
  private isRoleLevelCheck = false;
  private requireAllPermissions = false;
  private elseTemplate: TemplateRef<any> | null = null;
  private hasView = false;
  private subscription?: Subscription;

  constructor(
    private templateRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private permissionService: PermissionService,
    private profileService: ProfileService
  ) {}

  ngOnInit(): void {
    // Watch for user changes (login/logout/role changes)
    this.subscription = this.permissionService.permissions$.subscribe(() => {
      this.updateView();
    });
    this.updateView();
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private updateView(): void {
    const hasAccess = this.checkAccess();

    if (hasAccess && !this.hasView) {
      // Show the main template
      this.viewContainer.clear();
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (!hasAccess && this.hasView) {
      // Hide the main template
      this.viewContainer.clear();
      this.hasView = false;
      
      // Show else template if provided
      if (this.elseTemplate) {
        this.viewContainer.createEmbeddedView(this.elseTemplate);
      }
    } else if (!hasAccess && !this.hasView && this.elseTemplate) {
      // Show else template when we don't have access and no main view
      this.viewContainer.clear();
      this.viewContainer.createEmbeddedView(this.elseTemplate);
    }
  }

  private checkAccess(): boolean {
    if (this.permissions.length === 0) {
      return true; // No permissions specified = always show
    }

    if (this.isRoleCheck) {
      // Check for exact role match
      return this.permissions.some(role => this.permissionService.hasRole(role));
    }

    if (this.isRoleLevelCheck) {
      // Check for role level (user has this role or higher)
      return this.permissions.some(role => this.permissionService.hasRoleLevel(role));
    }

    // Check permissions
    if (this.requireAllPermissions) {
      return this.permissionService.hasAllPermissions(this.permissions);
    } else {
      return this.permissionService.hasAnyPermission(this.permissions);
    }
  }
}

/**
 * Pipe for checking permissions in templates
 */
import { Pipe, PipeTransform } from '@angular/core';

@Pipe({
  name: 'hasPermission',
  standalone: true,
  pure: false // Make it impure so it updates when user changes
})
export class HasPermissionPipe implements PipeTransform {
  constructor(private permissionService: PermissionService) {}

  transform(permission: string | string[], requireAll: boolean = false): boolean {
    if (!permission) return false;

    const permissions = Array.isArray(permission) ? permission : [permission];
    
    if (requireAll) {
      return this.permissionService.hasAllPermissions(permissions);
    } else {
      return this.permissionService.hasAnyPermission(permissions);
    }
  }
}

/**
 * Pipe for checking roles
 */
@Pipe({
  name: 'hasRole',
  standalone: true,
  pure: false
})
export class HasRolePipe implements PipeTransform {
  constructor(private permissionService: PermissionService) {}

  transform(role: string | string[], level: boolean = false): boolean {
    if (!role) return false;

    const roles = Array.isArray(role) ? role : [role];
    
    if (level) {
      // Check role level
      return roles.some(r => this.permissionService.hasRoleLevel(r));
    } else {
      // Check exact role
      return roles.some(r => this.permissionService.hasRole(r));
    }
  }
}